import React, { useState, useEffect } from 'react'
import { useSyncStatus } from '../hooks/useSyncStatus'
import { useSyncStore } from '../stores/useSyncStore'
import { useAppStore } from '../stores/useAppStore'
import { 
  ArrowPathIcon, 
  ExclamationTriangleIcon,
  DocumentTextIcon,
  PhotoIcon,
  ClipboardDocumentCheckIcon,
  TrashIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline'

const SyncManagement: React.FC = () => {
  const { getSyncSummary, syncAll, syncItem, clearFailed } = useSyncStatus()
  const { 
    isSyncing, 
    syncProgress, 
    lastSyncTime, 
    lastSyncError,
    totalPendingCount,
    failedItems
  } = useSyncStore()
  const { isOnline } = useAppStore()
  
  const [syncSummary, setSyncSummary] = useState<Awaited<ReturnType<typeof getSyncSummary>> | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'pending' | 'failed'>('pending')

  // Load sync summary
  useEffect(() => {
    //const loadSummary = async () => {
    //  setLoading(true)
    //  try {
    //    const summary = await getSyncSummary(true)
    //    setSyncSummary(summary)
    //  } finally {
    //    setLoading(false)
    //  }
      //}
      const loadSummary = async () => {
          try {
              const summary = await getSyncSummary(true)
              setSyncSummary(summary)
          } finally {
              setLoading(false)
          }
      }
    
    loadSummary()
    
    // Refresh every 5 seconds when syncing
    const interval = setInterval(loadSummary, isSyncing ? 5000 : 30000)
    return () => clearInterval(interval)
  }, [getSyncSummary, isSyncing])

  // Calculate totals
  const totalFailed = (failedItems.workOrderUpdates + failedItems.files + failedItems.jsaForms) || 0

  // Format date
  const formatDate = (date: Date | string | null) => {
    if (!date) return 'Never'
    const d = new Date(date)
    return d.toLocaleString()
  }

  // Format timestamp
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleString()
  }

  // Get icon for item type
  const getItemIcon = (type: string) => {
    switch (type) {
      case 'workorder':
      case 'note':
        return <DocumentTextIcon className="h-5 w-5" />
      case 'alarm':
        return <ClipboardDocumentCheckIcon className="h-5 w-5" />
      case 'photo':
      case 'file':
        return <PhotoIcon className="h-5 w-5" />
      case 'travel':
      case 'workOrder':
        return <ClipboardDocumentCheckIcon className="h-5 w-5" />
      default:
        return <DocumentTextIcon className="h-5 w-5" />
    }
  }

  // Get action label
  const getActionLabel = (action: string) => {
    switch (action) {
      case 'create': return 'Create'
      case 'update': return 'Update'
      case 'delete': return 'Delete'
      default: return action
    }
  }

  if (loading) {
    return (
      <div className="container-mobile py-6">
        <div className="flex items-center justify-center h-64">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </div>
    )
  }

  return (
    <div className="container-mobile py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sync Management</h1>
        <p className="text-gray-600">
          Manage offline data synchronization
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Pending Items */}
        {/* <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Pending Sync</h3>
            <ArrowUpTrayIcon className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{totalPendingCount}</div>
          <p className="text-xs text-gray-500 mt-1">Items waiting to sync</p>
        </div> */}

        {/* Failed Items */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Pending Items</h3>
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          </div>
          <div className="text-2xl font-bold text-gray-900">{totalFailed}</div>
          <p className="text-xs text-gray-500 mt-1">Items that pending to sync</p>
        </div>

        {/* Last Sync */}
        {/* <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Last Sync</h3>
            <ArrowPathIcon className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-sm font-medium text-gray-900">
            {formatDate(lastSyncTime)}
          </div>
          {lastSyncError && (
            <p className="text-xs text-red-600 mt-1">{lastSyncError}</p>
          )}
        </div> */}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={syncAll}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-smooth
            ${!isOnline || isSyncing || totalPendingCount === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }
          `}
        >
          <ArrowPathIcon className={`h-5 w-5 ${isSyncing ? 'animate-spin' : ''}`} />
          <span>{isSyncing ? `Syncing... ${syncProgress}%` : 'Sync All'}</span>
        </button>

        {/* {totalFailed > 0 && (
          <button
            onClick={clearFailed}
            className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium transition-smooth"
          >
            <TrashIcon className="h-5 w-5" />
            <span>Clear Failed</span>
          </button>
        )} */}

        {!isOnline && (
          <div className="flex items-center space-x-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg">
            <ExclamationTriangleIcon className="h-5 w-5" />
            <span className="font-medium">You are offline</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b mb-4">
        <nav className="flex space-x-8">
          {/* <button
            onClick={() => setActiveTab('pending')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm transition-smooth
              ${activeTab === 'pending'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            Pending ({totalPendingCount})
          </button> */}
          <button
            onClick={() => setActiveTab('failed')}
            className={`
              py-2 px-1 border-b-2 font-medium text-sm transition-smooth
              ${activeTab === 'failed'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            Pending ({totalFailed})
          </button>
        </nav>
      </div>

      {/* Items List */}
      <div className="space-y-4">
        {/* Work Order Updates */}
        {syncSummary?.workOrderUpdates.details && syncSummary.workOrderUpdates.details.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Work Order Updates</h3>
            <div className="space-y-2">
              {syncSummary.workOrderUpdates.details
                .filter(item => activeTab === 'pending' ? item.status !== 'failed' : item.status === 'failed')
                .map((item) => (
                  <div key={item.id} className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="mt-0.5">{getItemIcon(item.type)}</div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {item.workOrderNumber} - {getActionLabel(item.action)} {item.type}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {formatTimestamp(item.timestamp)}
                          </div>
                          {item.error && (
                            <div className="text-sm text-red-600 mt-1">{item.error}</div>
                          )}
                        </div>
                      </div>
                      {item.status === 'failed' && (
                        <button
                          onClick={() => syncItem(item.id, 'workorder')}
                          disabled={!isOnline}
                          className="text-sm text-primary hover:text-primary/80 font-medium disabled:text-gray-400"
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Files */}
        {syncSummary?.files.details && syncSummary.files.details.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Photos</h3>
            <div className="space-y-2">
              {syncSummary.files.details
                .filter(item => activeTab === 'pending' ? item.status !== 'failed' : item.status === 'failed')
                .map((item) => (
                  <div key={item.id} className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <PhotoIcon className="h-5 w-5 mt-0.5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {item.name}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {item.workOrderNumber} • {(item.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                          {item.error && (
                            <div className="text-sm text-red-600 mt-1">{item.error}</div>
                          )}
                        </div>
                      </div>
                      {item.status === 'failed' && item.id && (
                        <button
                          onClick={() => syncItem(item.id!, 'file')}
                          disabled={!isOnline}
                          className="text-sm text-primary hover:text-primary/80 font-medium disabled:text-gray-400"
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* JSA Forms */}
        {syncSummary?.jsaForms.details && syncSummary.jsaForms.details.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">JSA Forms</h3>
            <div className="space-y-2">
              {syncSummary.jsaForms.details
                .filter(item => activeTab === 'pending' ? item.status !== 'failed' : item.status === 'failed')
                .map((item) => (
                  <div key={item.id} className="bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <ClipboardDocumentCheckIcon className="h-5 w-5 mt-0.5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {item.type === 'travel' ? 'Travel JSA' : 'Work Order JSA'}
                            {item.workOrderNumber && ` - ${item.workOrderNumber}`}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {item.technicianName} • {new Date(item.date).toLocaleDateString()}
                          </div>
                          {item.error && (
                            <div className="text-sm text-red-600 mt-1">{item.error}</div>
                          )}
                        </div>
                      </div>
                      {item.status === 'failed' && (
                        <button
                          onClick={() => syncItem(item.id, 'jsa')}
                          disabled={!isOnline}
                          className="text-sm text-primary hover:text-primary/80 font-medium disabled:text-gray-400"
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Empty State */}
              {activeTab === 'pending' && syncSummary && totalPendingCount === 0 && (
          <div className="text-center py-12">
            <ArrowPathIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All synced!</h3>
            <p className="text-gray-500">No items waiting to sync</p>
          </div>
        )}
        
        {activeTab === 'failed' && totalFailed === 0 && (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No failed items</h3>
            <p className="text-gray-500">All sync attempts have succeeded</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default SyncManagement